import { makeStyles } from "@material-ui/core";

/**
 * iOS-inspired Glassmorphism Modal Styles
 */

// Glass color palette
const glassColors = {
  lightGlass: "rgba(255, 255, 255, 0.72)",
  separatorLight: "rgba(60, 60, 67, 0.12)",
  fillTertiary: "rgba(118, 118, 128, 0.12)",
  labelPrimary: "rgba(0, 0, 0, 0.85)",
  labelSecondary: "rgba(60, 60, 67, 0.60)",
  systemBlue: "#007AFF",
};

const modalStyles = makeStyles((theme) => ({
  // Glass backdrop with blur
  backdrop: {
    backgroundColor: "rgba(0, 0, 0, 0.4)",
    backdropFilter: "blur(4px)",
    WebkitBackdropFilter: "blur(4px)",
  },

  // Main glass modal paper
  paper: {
    background: "rgba(255, 255, 255, 0.85)",
    backdropFilter: "blur(40px) saturate(200%)",
    WebkitBackdropFilter: "blur(40px) saturate(200%)",
    boxShadow: "0 24px 80px rgba(0, 0, 0, 0.2)",
    borderRadius: 16,
    border: `0.5px solid rgba(255, 255, 255, 0.2)`,
    overflow: "hidden",
    transition: "all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
  },

  // Glass header
  headerWrap: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "20px 24px 16px",
    position: "relative",
    background: "rgba(255, 255, 255, 0.72)",
    backdropFilter: "blur(20px) saturate(180%)",
    WebkitBackdropFilter: "blur(20px) saturate(180%)",
    borderBottom: `0.5px solid ${glassColors.separatorLight}`,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    fontSize: "1.125rem",
    fontWeight: 600,
    letterSpacing: "-0.01em",
    color: glassColors.labelPrimary,
  },

  // Glass content area
  contentWrap: {
    padding: "24px",
    background: "rgba(255, 255, 255, 0.95)",
  },

  // Glass footer
  footerWrap: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 12,
    padding: "16px 24px",
    background: "rgba(255, 255, 255, 0.72)",
    backdropFilter: "blur(20px) saturate(180%)",
    WebkitBackdropFilter: "blur(20px) saturate(180%)",
    borderTop: `0.5px solid ${glassColors.separatorLight}`,
    borderBottomLeftRadius: 16,
    borderBottomRightRadius: 16,
  },

  // Alternative header styling
  modalHeaderWrap: {
    display: "flex",
    // justifyContent: "space-between",
    alignItems: "center",
    padding: "16px 20px",
    position: "relative",
    background: "rgba(255, 255, 255, 0.72)",
    backdropFilter: "blur(20px)",
    WebkitBackdropFilter: "blur(20px)",
    borderBottom: `0.5px solid ${glassColors.separatorLight}`,
  },

  modalHeader: {
    padding: "20px 24px 16px",
    background: "rgba(255, 255, 255, 0.72)",
    backdropFilter: "blur(20px) saturate(180%)",
    WebkitBackdropFilter: "blur(20px) saturate(180%)",
    borderBottom: `0.5px solid ${glassColors.separatorLight}`,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    fontSize: "1.125rem",
    fontWeight: 600,
    letterSpacing: "-0.01em",
    color: glassColors.labelPrimary,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },

  modalContent: {
    padding: "24px",
    background: "rgba(255, 255, 255, 0.95)",
  },

  modalFooter: {
    padding: "16px 24px",
    background: "rgba(255, 255, 255, 0.72)",
    backdropFilter: "blur(20px) saturate(180%)",
    WebkitBackdropFilter: "blur(20px) saturate(180%)",
    borderTop: `0.5px solid ${glassColors.separatorLight}`,
    borderBottomLeftRadius: 16,
    borderBottomRightRadius: 16,
    display: "flex",
    justifyContent: "flex-end",
  },

  // iOS-style action buttons
  actionButton: {
    background: glassColors.fillTertiary,
    color: glassColors.systemBlue,
    border: "none",
    borderRadius: 10,
    padding: "10px 20px",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.2s ease",
    "&:hover": {
      background: "rgba(0, 122, 255, 0.12)",
    },
    "&:active": {
      transform: "scale(0.97)",
    },
  },

  primaryButton: {
    background: glassColors.systemBlue,
    color: "#fff",
    border: "none",
    borderRadius: 10,
    padding: "10px 20px",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.2s ease",
    "&:hover": {
      background: "#0066E1",
      boxShadow: "0 4px 16px rgba(0, 122, 255, 0.3)",
    },
    "&:active": {
      transform: "scale(0.97)",
    },
  },

  secondaryButton: {
    background: glassColors.fillTertiary,
    color: glassColors.labelPrimary,
    border: "none",
    borderRadius: 10,
    padding: "10px 20px",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.2s ease",
    "&:hover": {
      background: "rgba(120, 120, 128, 0.18)",
    },
    "&:active": {
      transform: "scale(0.97)",
    },
  },

  // Close button
  closeButton: {
    width: 32,
    height: 32,
    borderRadius: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: glassColors.fillTertiary,
    border: "none",
    cursor: "pointer",
    color: glassColors.labelSecondary,
    position: "absolute",
    right: 16,
    top: "50%",
    transform: "translateY(-50%)",
    transition: "all 0.2s ease",
    "&:hover": {
      background: "rgba(120, 120, 128, 0.2)",
      color: glassColors.labelPrimary,
    },
  },

  // Modal title
  modalTitle: {
    fontSize: "1.125rem",
    fontWeight: 600,
    color: glassColors.labelPrimary,
    letterSpacing: "-0.01em",
    margin: 0,
  },

  modalSubtitle: {
    fontSize: "0.8125rem",
    color: glassColors.labelSecondary,
    fontWeight: 500,
    marginTop: 4,
  },
}));

export default modalStyles;
